class FullBleedBannerMedia extends HTMLElement {
  #abort;
  #posterDelayElapsed = false;
  #posterTimer;
  #reducedMotion;
  #video;

  connectedCallback() {
    if (this.#abort) return;

    this.#video = this.querySelector('video');
    if (!this.#video) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#onMotionPreferenceChange, { signal });
    this.#video.addEventListener('playing', this.#onPlaying, { signal });

    const posterMinDuration = Number.parseFloat(this.dataset.posterMinDuration) || 0;
    this.#posterDelayElapsed = posterMinDuration <= 0;

    if (this.#posterDelayElapsed) {
      this.#play();
    } else {
      this.#posterTimer = window.setTimeout(this.#onPosterDelayElapsed, posterMinDuration * 1000);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
    window.clearTimeout(this.#posterTimer);
    this.#posterTimer = undefined;
    this.#posterDelayElapsed = false;
    this.#video?.pause();
    this.#video = undefined;
    this.#reducedMotion = undefined;
    this.removeAttribute('data-playing');
  }

  #onPosterDelayElapsed = () => {
    this.#posterTimer = undefined;
    this.#posterDelayElapsed = true;
    this.#play();
  };

  #onPlaying = () => {
    this.toggleAttribute('data-playing', true);
  };

  #onMotionPreferenceChange = () => {
    if (this.#reducedMotion?.matches) {
      this.#video?.pause();
      this.removeAttribute('data-playing');
      return;
    }

    this.#play();
  };

  #play() {
    if (!this.#video || !this.#posterDelayElapsed || this.#reducedMotion?.matches) return;

    this.#video.play().catch(() => {
      this.removeAttribute('data-playing');
    });
  }
}

class FullBleedBannerCta extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {HTMLElement | null} */
  #lastFocused = null;

  /** @type {HTMLDialogElement | null} */
  #popupEl = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const popup = this.#section?.querySelector('[data-full-bleed-youtube-popup]') ?? null;
    this.#popupEl = popup instanceof HTMLDialogElement ? popup : null;

    this.addEventListener('click', this.#onClick, { signal });
    this.#popupEl?.addEventListener('click', this.#onPopupClick, { signal });
    this.#popupEl?.addEventListener('close', this.#onDialogClose, { signal });
    this.#popupEl
      ?.querySelector('[data-full-bleed-youtube-close]')
      ?.addEventListener('click', this.#onCloseButton, { signal });
    this.#bindYoutubeTrigger();
  }

  disconnectedCallback() {
    this.#closePopup(false);
    this.#abort?.abort();
    this.#abort = undefined;
    this.#popupEl = null;
    this.#lastFocused = null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const videoTrigger = event.target.closest('a, button');
    if (
      videoTrigger &&
      this.contains(videoTrigger) &&
      (this.dataset.youtubeTriggerMode !== 'outline-only' ||
        videoTrigger.classList.contains('button--outline-white'))
    ) {
      const videoId = this.#videoId();
      if (videoId) {
        event.preventDefault();
        this.#openYoutube(videoId, videoTrigger);
        return;
      }
    }

    const trigger = event.target.closest('[data-anchor-target-section]');
    if (!trigger || !this.contains(trigger)) return;

    const sectionId = trigger.getAttribute('data-anchor-target-section')?.trim();
    if (!sectionId) return;

    const hrefTargetId = trigger.getAttribute('href')?.replace(/^#/, '');
    const currentSection = this.closest('[id^="shopify-section-"]');
    const currentSectionId = currentSection?.id.replace(/^shopify-section-/, '');
    const templatePrefix = currentSectionId?.split('__')[0];
    const target =
      (hrefTargetId ? document.getElementById(hrefTargetId) : null) ||
      (templatePrefix ? document.getElementById(`shopify-section-${templatePrefix}__${sectionId}`) : null) ||
      document.getElementById(`shopify-section-${sectionId}`) ||
      document.getElementById(sectionId);
    if (!target) return;

    event.preventDefault();

    const tabs = document.querySelector(
      '[id^="shopify-section-"][id$="__anchor-tab-responsive"], #shopify-section-anchor-tab-responsive',
    );
    const offset = tabs instanceof HTMLElement ? tabs.offsetHeight : 0;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  get #section() {
    return this.closest('[id^="shopify-section-"]');
  }

  get #popup() {
    return this.#popupEl;
  }

  get #player() {
    return this.#popup?.querySelector('[data-full-bleed-youtube-player]') ?? null;
  }

  get #heroVideo() {
    return this.closest('.full-bleed-banner')?.querySelector('.full-bleed-banner__video') ?? null;
  }

  #bindYoutubeTrigger() {
    const popup = this.#popupEl;
    if (!popup?.id) return;

    const triggers =
      this.dataset.youtubeTriggerMode === 'outline-only'
        ? this.querySelectorAll('.button--outline-white')
        : this.querySelectorAll('a, button');

    triggers.forEach((trigger) => {
      if (!this.contains(trigger)) return;
      trigger.setAttribute('aria-controls', popup.id);
      if (!trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /**
   * @param {string} videoId
   * @param {Element} trigger
   */
  #openYoutube(videoId, trigger) {
    const popup = this.#popup;
    const player = this.#player;
    if (!(popup instanceof HTMLDialogElement) || !(player instanceof HTMLIFrameElement)) return;

    this.#lastFocused = trigger instanceof HTMLElement ? trigger : null;
    trigger.setAttribute('aria-expanded', 'true');
    player.src = this.#embedUrl(videoId);
    if (!popup.open) popup.showModal();
    popup.querySelector('[data-full-bleed-youtube-close]')?.focus({ preventScroll: true });
    this.#heroVideo?.pause();
  }

  #onCloseButton = () => {
    this.#closePopup();
  };

  /**
   * @param {MouseEvent} event
   */
  #onPopupClick = (event) => {
    if (event.target === this.#popup) this.#closePopup();
  };

  #onDialogClose = () => {
    const player = this.#player;
    if (player instanceof HTMLIFrameElement) player.removeAttribute('src');
    this.#trigger()?.setAttribute('aria-expanded', 'false');
    this.#resumeHeroVideo();
  };

  /**
   * @param {boolean} [restoreFocus]
   */
  #closePopup(restoreFocus = true) {
    const popup = this.#popup;
    if (!(popup instanceof HTMLDialogElement)) return;
    if (popup.open) popup.close();
    if (restoreFocus) this.#lastFocused?.focus?.({ preventScroll: true });
  }

  #resumeHeroVideo() {
    const video = this.#heroVideo;
    if (!video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    video.play().catch(() => {});
  }

  #trigger() {
    return this.querySelector('[aria-controls]');
  }

  #videoId() {
    let source;
    if (window.innerWidth >= 1280) {
      source = this.dataset.youtubePc || this.dataset.youtubeTablet || this.dataset.youtubeMobile;
    } else if (window.innerWidth >= 768) {
      source = this.dataset.youtubeTablet || this.dataset.youtubePc || this.dataset.youtubeMobile;
    } else {
      source = this.dataset.youtubeMobile || this.dataset.youtubeTablet || this.dataset.youtubePc;
    }

    return this.#normalizeVideoId(source);
  }

  /**
   * @param {string | undefined} source
   */
  #normalizeVideoId(source) {
    if (!source) return '';

    try {
      const url = new URL(source);
      if (url.hostname.includes('youtu.be')) return url.pathname.slice(1);
      return url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop() || '';
    } catch {
      return source;
    }
  }

  /**
   * @param {string} videoId
   */
  #embedUrl(videoId) {
    const parameters = new URLSearchParams({
      playsinline: '1',
      rel: '0',
      modestbranding: '1',
      enablejsapi: '1',
      origin: window.location.origin,
      autoplay: '1',
      controls: '1',
    });

    return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${parameters.toString()}`;
  }
}

if (!customElements.get('full-bleed-banner-media')) {
  customElements.define('full-bleed-banner-media', FullBleedBannerMedia);
}

if (!customElements.get('full-bleed-banner-cta')) {
  customElements.define('full-bleed-banner-cta', FullBleedBannerCta);
}
